import { memo } from 'react';
import { TileDataSheetBackedWithSheet } from '@common/api';
import { FinderProvider } from '@features/finder/FinderProvider';
import { SheetEditorContext } from '@features/sheet/editor/SheetEditorProvider';
import { useSheetState } from '@features/sheet/editor/sheetState';
import { SheetEditorBreadcrumbs } from '@features/sheet/editor/SheetEditorBreadcrumbs';
import { FlexCol } from '@components/layout/Flex';
import { SheetEditorTable } from '@features/sheet/editor/SheetEditorTable';
import { DashboardTileContent } from '../Dashboard.style';
import { GridLayoutIgnoreDragContainer } from '@components/grid-layout';
import { useTileGlobalOverride } from './useTileGlobalOverride';
import { useTileConfigSync } from './useTileConfig';
import { SheetEditorTimeline } from '@features/sheet/editor/SheetEditorTimeline';
import { PageSheet } from '@components/page/PageSheet';
import { SheetEditorControls } from '@features/sheet/editor/SheetEditorControls';

export const SheetTile = memo(
    ({
        title,
        data,
        canEdit,
        canDataEntry,
    }: {
        title: string;
        data: TileDataSheetBackedWithSheet;
        canEdit: boolean;
        canDataEntry: boolean;
    }) => {
        const globalOverride = useTileGlobalOverride(data);

        const sheetPath = `${data.sheet.id}`;
        const sheetContext = useSheetState(sheetPath, canDataEntry, canEdit, globalOverride);

        const {
            state: {
                sheetRowPaths: { focused: focusedSheetRowPath },
                sheetConfig: {
                    columns: {
                        intervalToolbar: { enabled: intervalToolbarEnabled },
                    },
                },
                canSaveForEveryone,
            },
            api: { resetConfig, saveForEveryone },
        } = sheetContext;

        useTileConfigSync(canSaveForEveryone, saveForEveryone, resetConfig);

        return (
            <SheetEditorContext.Provider value={sheetContext}>
                <FinderProvider>
                    <FlexCol $flexGrow $gap={8}>
                        <SheetEditorBreadcrumbs title={title} isSheetTile />
                        <FlexCol $flexGrow $gap={6}>
                            <DashboardTileContent style={{ gap: 6 }}>
                                <SheetEditorControls
                                    showSort={!data.useDashboardSort}
                                    showFilters={!data.useDashboardFilters}
                                    showColumnManager
                                    showExportButton
                                    showExpressionButton
                                    showForecastButton
                                />
                                <PageSheet>
                                    <GridLayoutIgnoreDragContainer>
                                        {/* Remount sheet when focus changes to reset scroll state */}
                                        <SheetEditorTable key={focusedSheetRowPath} />
                                    </GridLayoutIgnoreDragContainer>
                                </PageSheet>
                                {!data.useDashboardTimeline && intervalToolbarEnabled ? <SheetEditorTimeline /> : null}
                            </DashboardTileContent>
                        </FlexCol>
                    </FlexCol>
                </FinderProvider>
            </SheetEditorContext.Provider>
        );
    }
);
SheetTile.displayName = 'SheetTile';
